'use client';

import React from 'react';
import { m } from 'framer-motion';
import { Search, MessageSquare, Zap, Target, ArrowUpRight, Cpu } from 'lucide-react';
import Link from 'next/link';

const BentoCard = ({ 
    children, 
    className = "", 
    title, 
    subtitle, 
    icon: Icon, 
    tag 
}: { 
    children: React.ReactNode, 
    className?: string, 
    title: string, 
    subtitle: string, 
    icon: any, 
    tag?: string 
}) => (
    <m.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className={`group relative overflow-hidden bg-white border border-neutral-200 rounded-3xl p-6 sm:p-8 flex flex-col hover:border-neutral-300 transition-all shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-[0_10px_40px_rgba(0,0,0,0.04)] ${className}`}
    >
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-neutral-900 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-white" />
                </div>
                {tag && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-neutral-100 text-neutral-500 uppercase tracking-widest leading-none">
                        {tag}
                    </span>
                )}
            </div>
            <ArrowUpRight size={18} className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0" />
        </div>
        
        <div className="mb-8">
            <h3 className="text-2xl font-bold text-neutral-900 mb-2 leading-tight tracking-tight font-sans">
                {title}
            </h3>
            <p className="text-[15px] text-neutral-500 leading-relaxed font-medium">
                {subtitle}
            </p>
        </div>

        <div className="flex-1 min-h-[120px] relative">
            {children}
        </div>
    </m.div>
);

export const FeatureGrid = () => {
    return (
        <section id="features" className="bg-white py-24 sm:py-32 overflow-hidden border-t border-neutral-100">
            <div className="max-w-[1100px] mx-auto px-6">
                
                {/* Section Header */}
                <m.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="max-w-3xl mb-16 sm:mb-20"
                >
                    <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-black mb-5">Acquisition Engine</p>
                    <h2 className="text-4xl sm:text-6xl font-extrabold text-neutral-900 mb-6 tracking-tight leading-[1.05] font-sans">
                        Revenue generation.<br />
                        <span className="text-neutral-400">Total autonomy.</span>
                    </h2>
                    <p className="text-lg sm:text-xl text-neutral-500 leading-relaxed max-w-2xl font-medium">
                        Forget managing agencies and hiring sales reps. Axist is a closed-loop system that finds, intercepts, and closes buyers 24/7.
                    </p>
                </m.div>

                {/* Bento Grid layout — Responsive grid system */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    
                    {/* Intercept competitors */}
                    <BentoCard 
                        className="md:col-span-8 bg-[#FAFAFA]/50" 
                        title="Intercept Competitors' Losses" 
                        subtitle="Strike while intent is at its peak. Sentinel listens for competitor complaints in real-time and executes immediate, empathetic defection offers."
                        icon={Target}
                        tag="Live listening"
                    >
                        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mt-4">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                                <span className="text-[11px] font-bold text-neutral-400 uppercase tracking-widest">Real-time Intercepts</span>
                                <div className="flex gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                                </div>
                            </div>
                            <div className="p-4 space-y-4">
                                <div className="flex gap-3 items-start opacity-40 blur-[0.5px]">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 shrink-0" />
                                    <div className="space-y-1.5 flex-1">
                                        <div className="h-2.5 bg-slate-100 rounded w-24" />
                                        <div className="h-2 bg-slate-50 rounded" />
                                    </div>
                                </div>
                                <div className="flex gap-3 items-start p-3 bg-rose-50/30 border border-rose-100 rounded-xl relative scale-[1.02] shadow-sm">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                                        <span className="text-[10px] font-bold text-indigo-600 tracking-tight leading-none uppercase">JD</span>
                                    </div>
                                    <div className="space-y-2 flex-1 min-w-0">
                                        <div className="flex justify-between items-center">
                                            <p className="text-[11px] font-bold text-rose-600">HOT DEFECTION SIGNAL</p>
                                            <span className="text-[10px] font-mono text-neutral-400">2m ago</span>
                                        </div>
                                        <p className="text-[12px] text-neutral-800 leading-snug font-medium line-clamp-2">
                                            &quot;I am so tired of [Competitor X]&apos;s downtime. Looking for an alternative that actually scales.&quot;
                                        </p>
                                        <div className="flex gap-2">
                                            <div className="h-6 px-2 rounded-md bg-rose-500 flex items-center justify-center">
                                                <span className="text-[10px] font-bold text-white tracking-tight leading-none uppercase">Deploy Archer</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </BentoCard>

                    {/* Omnichannel Mastery */}
                    <BentoCard 
                        className="md:col-span-4" 
                        title="Be Everywhere." 
                        subtitle="Scale your presence across X, LinkedIn, and Email without lifting a finger."
                        icon={MessageSquare}
                        tag="Omnichannel"
                    >
                        <div className="relative mt-4 space-y-4">
                            {[
                                { platform: 'LinkedIn', icon: 'LI', color: 'bg-blue-600', val: 'Scheduled · 12:00PM' },
                                { platform: 'Twitter/X', icon: 'X', color: 'bg-neutral-900', val: 'Published · 2m ago' },
                                { platform: 'Email', icon: '📩', color: 'bg-indigo-600', val: '50 Out reaching...' }
                            ].map((p, i) => (
                                <div key={p.platform} className="flex items-center gap-3 p-3 bg-white border border-neutral-100 rounded-2xl shadow-sm hover:border-neutral-200 transition-colors">
                                     <div className={`w-8 h-8 rounded-lg ${p.color} flex items-center justify-center shrink-0 text-white text-[10px] font-black`}>
                                        {p.icon}
                                     </div>
                                     <div className="min-w-0">
                                         <p className="text-[12px] font-bold text-neutral-900 tracking-tight leading-tight">{p.platform}</p>
                                         <p className="text-[10px] text-neutral-400 font-medium tracking-tight mt-0.5">{p.val}</p>
                                     </div>
                                </div>
                            ))}
                        </div>
                    </BentoCard>

                    {/* Personalization Mastery */}
                    <BentoCard 
                        className="md:col-span-5" 
                        title="Researched Outreach" 
                        subtitle="Every message Archer sends is written from fresh lead intel. Zero stale templates."
                        icon={Zap}
                        tag="Deep Personalization"
                    >
                         <div className="mt-4 p-4 rounded-2xl bg-neutral-900 text-white font-mono text-[11px] space-y-3 shadow-inner">
                            <div className="flex gap-2 text-rose-400">
                                <span>&gt;</span>
                                <span className="animate-pulse">Scoping lead...</span>
                            </div>
                            <p className="text-neutral-400 leading-relaxed italic">
                                &quot;Saw your news about the AI funding round. Congrats on the launch...&quot;
                            </p>
                            <div className="h-[1px] bg-neutral-800" />
                            <div className="flex gap-2 text-emerald-400">
                                <span>&gt;</span>
                                <span>Hook generated (High Impact)</span>
                            </div>
                         </div>
                    </BentoCard>

                    {/* Self-Learning DNA */}
                    <BentoCard 
                        className="md:col-span-7 bg-[#FAFAFA]/40" 
                        title="The Machine that gets smarter" 
                        subtitle="The Darwin Learning Loop aggregates every outcome into structural truths that permanently update your Brand DNA."
                        icon={Cpu}
                        tag="Evolutionary"
                    >
                        <div className="mt-4 grid grid-cols-2 gap-4">
                             <div className="bg-white p-4 rounded-2xl border border-neutral-200 shadow-sm flex flex-col justify-between">
                                 <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-4">Darwin Insight</p>
                                 <p className="text-[13px] font-bold text-neutral-900 tracking-tight leading-[1.2]">
                                     &quot;Short subject lines result in 2.3× more replies.&quot;
                                 </p>
                             </div>
                             <div className="bg-indigo-600 p-4 rounded-2xl shadow-lg flex flex-col justify-between text-white">
                                 <Cpu size={14} className="mb-4" />
                                 <p className="text-[13px] font-black tracking-tight leading-none">
                                     UPDATE DNA
                                 </p>
                                 <p className="text-[10px] opacity-70 mt-1 uppercase font-bold tracking-widest">Applying permanently</p>
                             </div>
                        </div>
                    </BentoCard>

                </div>
                
                {/* Section Bottom CTA */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="mt-20 text-center"
                >
                    <Link href="/login?mode=signup">
                        <button className="px-8 py-4 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white text-[15px] font-bold transition-all shadow-xl shadow-neutral-200 hover:-translate-y-0.5">
                            Start your autonomous mission
                        </button>
                    </Link>
                </m.div>

            </div>
        </section>
    );
};
